import { liveWorkSchema } from '@/features/veluvit/schemas/catalog.schema'
import { ownedCardSchema } from '@/features/veluvit/schemas/api.schema'

import type {
  Bootstrap,
  OwnedCard,
  Work,
} from '@/features/veluvit/types/api.types'
import type {
  Catalog,
  Char,
  Intro,
  Lane,
  SeqRow,
  Stage,
} from '@/features/veluvit/types/catalog.types'

const EMPTY_STAGE: Stage = { k: '', min: 0, label: '', color: '', tone: '' }
const MODE_ICONS: Record<string, string> = {
  daily: '☕',
  thrill: '💗',
  deep: '🌙',
  direct: '🎬',
}
const MODE_DESCRIPTIONS: Record<string, string> = {
  daily: '지금 하는 일과 주변의 구체적인 생활을 이어가요.',
  thrill: '인물 사이의 거리와 서로를 향한 관심을 살펴요.',
  deep: '앞선 사건과 약속이 지금의 행동으로 이어지게 해요.',
  direct: '공간의 감각과 인물 행동으로 장면을 선명하게 해요.',
}

/**
 * 성향 — 작품이 정한 f · m 이 먼저, 다음은 타깃. 새 스튜디오 작품은 서버가 lane 을 늘 'a' 로 만들고
 * 타깃(공개 정보의 여성향 · 남성향)을 female · male 로 실어 보낸다 — 그래서 'a' 보다 타깃을 앞에 본다
 */
function laneOf(work: Work): Lane {
  if (work.lane === 'f' || work.lane === 'm') return work.lane
  if (work.target === '여성향' || work.target === 'female') return 'f'
  if (work.target === '남성향' || work.target === 'male') return 'm'
  return 'a'
}

/**
 * 19+ 작품인가 — 서버 목록 필터(community/service.py restricted)와 같은 기준. 옛 작품은 style.rating,
 * 새 스튜디오 작품은 공개본에서 style 이 지워져 adultOnly · rating("19+")으로만 온다
 */
const ADULT_RATINGS = ['adult', 'unsafe', '19', '19+']
export const isAdult = (work: Work) =>
  work.adultOnly === true ||
  [work.style?.rating, work.rating].some(
    (r) => !!r && ADULT_RATINGS.includes(r.toLowerCase())
  )

function imageMap(work: Work): Record<string, string> {
  const image = work.image?.trim()
  const images = Object.fromEntries(
    Object.entries(work.images || {})
      .filter((entry) => typeof entry[1] === 'string' && !!entry[1].trim())
      .map(([key, value]) => [key, value.trim()])
  )
  // 채팅 프사는 작품 표지와 별개다. 등록한 얼굴 사진이 일반 이미지 표에 덮이지 않게 한다.
  const face =
    work.cast?.[0]?.avatar?.trim() ||
    images.avatar ||
    work.cast?.[0]?.image?.trim() ||
    image
  const title = work.titleImage?.trim() || images.logo
  return {
    ...(image ? { thumb: image, cover: image } : {}),
    ...images,
    ...(title ? { logo: title } : {}),
    ...(face ? { avatar: face } : {}),
  }
}

function introsOf(work: Work): Intro[] {
  const speakerName = (id: string | null | undefined) =>
    id ? work.cast?.find((person) => person.id === id)?.name : undefined
  if (work.starts?.length)
    return work.starts.map((start, index) => ({
      id: start.id || `start-${index}`,
      title: start.name,
      place: start.place,
      suggestions: start.suggestions || [],
      // 사진 줄(image 가 있는 줄)은 장면 컷 한 장 — 대화방 목록(모바일) · 왼쪽 무대(PC)에 뜬다. 그 줄의 글은 없다(sync.ts 가 비워 보낸다)
      seq: (start.intro?.lines || []).map((line): SeqRow =>
        line.image
          ? ['image', line.image]
          : [
              line.who === 'narration' ? 'sit' : line.who,
              line.text,
              line.who === 'char' ? speakerName(line.charId) || null : null,
            ]
      ),
    }))
  return work.openings.map((opening) => ({
    id: opening.id,
    title: opening.title,
    desc: opening.description,
    place: opening.place,
    chapterIndex: opening.chapterIndex,
    suggestions: opening.suggestions || [],
    seq: (opening.segments || []).flatMap((segment): SeqRow[] => {
      if (segment.type === 'speech' && segment.text)
        return [
          [
            'char',
            segment.text,
            segment.speaker || speakerName(segment.speakerId) || null,
          ],
        ]
      if (segment.type === 'user' && segment.text)
        return [['user', segment.text]]
      if (segment.type === 'narration' && segment.text)
        return [['sit', segment.text]]
      return []
    }),
  }))
}

function charOf(
  work: Work,
  userName: string,
  images: Record<string, string>
): Char {
  const lane = laneOf(work)
  const status = work.initialStatus
  const starts = introsOf(work)
  const cast = work.cast || []
  const personas =
    work.personas
      ?.filter((persona) => !!persona.name.trim())
      .map((persona) => ({
        name: persona.name,
        role: persona.role || '페르소나',
        desc: persona.desc || persona.description || '',
        gender: persona.gender,
        image: persona.image,
      })) || []
  return {
    id: work.id,
    name: work.name,
    // 부르는 이름 — 작품 제목이 아니라 주인공(cast 첫 번째). 출연진이 없을 때만 제목으로 대신한다
    short: cast[0]?.name?.trim() || work.name,
    age: typeof cast[0]?.age === 'number' ? cast[0].age : null,
    lane,
    laneLabel: lane === 'f' ? '여성향' : lane === 'm' ? '남성향' : '전체',
    // 1차는 캐릭터챗만 공개한다(스토리챗 메뉴 없음) — 서버 kind 와 상관없이 모두 캐릭터챗으로 본다.
    // 서버는 새 스튜디오 작품을 늘 kind: "story" 로 내려 그대로 나누면 어디에도 안 보인다(backend9.md 1). 2차 스토리챗을 열 때 kind 로 되돌린다
    format: '캐릭터챗',
    genre: work.genre || '',
    work: work.name,
    workId: work.id,
    author: work.creator?.name || '',
    official: work.official === true,
    ptype: '캐릭터챗',
    hook: work.description || '',
    hookLong: work.description || '',
    quote: '',
    subtitle: work.subtitle,
    tags: work.tags || [],
    badges: isAdult(work) ? ['19+'] : [],
    likes: work.stats?.likes ?? 0,
    chats: work.stats?.chats ?? 0,
    views: work.stats?.views ?? 0,
    hasHero: !!images.hero,
    imgKinds: Object.fromEntries(Object.keys(images).map((kind) => [kind, 1])),
    startAffinity: work.initialAffinity ?? 0,
    roadmap: null,
    calls: null,
    places: (work.places || []).map((place) =>
      typeof place === 'string'
        ? { name: place, total: 0 }
        : { name: place.name, desc: place.desc, total: 0 }
    ),
    placeSeed: {},
    played: [],
    intros: starts,
    status: {
      date: status?.date || '',
      time: status?.minutes ?? status?.time ?? null,
      noTime:
        status?.noTime ?? (status?.minutes == null && status?.time == null),
      place: status?.place || work.initialPlace || '',
      weather: status?.weather,
      expr: status?.expression,
      outfit: status?.outfit,
      inner: status?.inner,
    },
    sugs: [],
    same: [],
    personas: personas.length
      ? personas
      : [{ name: userName, role: '내 페르소나', desc: '' }],
    cast: cast.map((person) => ({
      name: person.name,
      age: person.age ?? undefined,
      role: person.role,
    })),
    style: work.style,
    v2Published: true,
  }
}

/**
 * [임시] 홈 전체 노출 — 스키마에 안 맞는 작품도 버리지 않는다. 필드마다 따로 검사해 맞는 것만 남기고,
 * 이름이 없으면 id, kind 가 없으면 캐릭터로 채운다. id 조차 없으면 버린다. 홈의 노출 여부와 순서는 별도 서버 홈 응답이 정한다.
 */
function lenientWork(candidate: unknown): Work | null {
  if (!candidate || typeof candidate !== 'object') return null
  const raw = candidate as Record<string, unknown>
  const id =
    typeof raw.id === 'string' || typeof raw.id === 'number'
      ? String(raw.id).trim()
      : ''
  if (!id) return null
  const kept = Object.fromEntries(
    Object.entries(liveWorkSchema.shape).flatMap(([key, field]) => {
      const result = field.safeParse(raw[key])
      return result.success && result.data !== undefined
        ? [[key, result.data]]
        : []
    })
  )
  const name =
    typeof raw.name === 'string' && raw.name.trim() ? raw.name.trim() : id
  return {
    ...kept,
    id,
    name,
    kind: kept.kind ?? 'character',
    openings: kept.openings ?? [],
  } as Work
}

/** 공개 Bootstrap만 화면 뷰모델로 투영한다. 잘못된 작품 하나는 다른 작품을 가리지 않는다. */
export function buildLiveCatalog(
  works: Work[],
  config: Bootstrap['config'],
  userName = '사용자',
  ownedCards: OwnedCard[] = []
): Catalog {
  const ownedImages = new Map<string, string>()
  for (const candidate of ownedCards) {
    const parsed = ownedCardSchema.safeParse(candidate)
    if (!parsed.success) continue
    const { workId, cardId, image } = parsed.data
    if (image) ownedImages.set(`${workId}:${cardId}`, image)
  }
  const validWorks: Work[] = []
  const seen = new Set<string>()
  for (const candidate of works) {
    const result = liveWorkSchema.safeParse(candidate)
    const work = result.success ? (result.data as Work) : lenientWork(candidate)
    if (!work || seen.has(work.id)) continue
    seen.add(work.id)
    validWorks.push({
      ...work,
      cards: work.cards?.map((card) => {
        const image = ownedImages.get(`${work.id}:${card.id}`)
        return image ? { ...card, image, locked: false } : card
      }),
    })
  }

  const images = Object.fromEntries(
    validWorks.map((work) => [work.id, imageMap(work)])
  )
  const chars = Object.fromEntries(
    validWorks.map((work) => [work.id, charOf(work, userName, images[work.id])])
  )
  const stagesById = Object.fromEntries(
    validWorks
      .filter((work) => work.stages?.length)
      .map((work) => [
        work.id,
        work.stages!.map((stage) => ({
          ...stage,
          color: stage.color || '',
          tone: stage.tone || '',
        })),
      ])
  )
  const modes = (config.modes || []).map((mode) => ({
    k: mode.id,
    name: mode.name,
    cost: mode.cost,
    icon: MODE_ICONS[mode.id] || '💬',
    desc: MODE_DESCRIPTIONS[mode.id] || '대화 방식을 선택해요.',
  }))

  return {
    user: userName,
    tiers: [],
    stages: [EMPTY_STAGE],
    modes,
    commands: [],
    charge: [],
    attend: [],
    personaRandom: [],
    comments: {},
    fictions: [],
    users: [],
    missions: [],
    gifts: [],
    giftDayTypical: 0,
    order: validWorks.map((work) => work.id),
    chars,
    cards: {},
    chapters: {},
    endings: {},
    stagesById,
    episodesById: {},
    cardPlaceOverrides: {},
    relationships: {},
    roadmaps: {},
    images,
    works: validWorks,
  }
}
